import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils.js";
import { AnimatePresence, motion } from "framer-motion";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  wide?: boolean;
}

export function Modal({ open, onClose, title, children, wide }: ModalProps) {
  const modalRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Escape key
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Focus trap
  React.useEffect(() => {
    if (!open || !modalRef.current) return;
    const el = modalRef.current;
    const focusable = el.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable.length) focusable[0].focus();

    const trap = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    el.addEventListener("keydown", trap);
    return () => el.removeEventListener("keydown", trap);
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-ink/30" onClick={onClose} />
          <motion.div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className={cn(
              "relative bg-card rounded-t-lg md:rounded-lg w-full max-h-[85vh] overflow-y-auto shadow-xl border border-clay-border",
              wide ? "md:max-w-2xl" : "md:max-w-md",
            )}
          >
            {title && (
              <div className="sticky top-0 bg-card flex items-center justify-between p-4 border-b border-clay-border z-10">
                <h2 className="font-headline text-xl font-semibold text-ink tracking-[-0.01em]">{title}</h2>
                <button onClick={onClose} aria-label="Close" className="p-2.5 -mr-1 rounded-md text-faint hover:bg-amethyst-soft hover:text-amethyst-ink transition-colors"><X className="w-5 h-5" /></button>
              </div>
            )}
            {/* Bottom padding reserves room for the mobile bottom nav (z-50) + the
                iOS home-indicator safe area so a bottom-anchored sheet's primary CTA
                is never occluded or tucked under the notch. The modal itself sits at
                z-[60], above the nav. Desktop (md) has no bottom nav, so pb-4. */}
            <div className="p-4 pb-[calc(env(safe-area-inset-bottom)_+_5rem)] md:pb-4">{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
